import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Check, Copy, Loader2, QrCode, RefreshCw, Search, ShieldCheck, Trash2, X } from "lucide-react";
import { usePage, type SectionProps } from "./context";
import { Band, Head, cn, rv, str } from "./ui";
import { channelCatalog, channelCategories } from "@/mocks/channels";
import ChannelWizard, { type WizardChannel } from "./channel-wizard";
import ChannelPicker, { type ChannelPick, type PickerFamily } from "./channel-picker";
import { CHANNEL_META, WIZARD_CATALOG_ID } from "./channel-wizard-kit";
import { apiErrorText } from "@/lib/api";
import { onServerEvent } from "@/lib/events";
import { t } from "@/lib/i18n";
import { CHANNEL_STATUS_LABEL, channelsApi, type Channel as HubChannel } from "./inbox-api";
import { relTime } from "./contactsapp-meta";

/* «Каталог каналов»: слева категории, справа карточки каналов.
   WhatsApp (QR-код или код по номеру) и личный Telegram (QR-код) подключаются по-настоящему — через шлюзы; подключённые
   аккаунты показаны отдельным списком со статусом и кнопками «Переподключить» / «Отключить» / «Удалить».
   Остальные каналы пока имитируются в макете, об этом честно написано под окном. */

const LIVE_KINDS = new Set(["whatsapp", "telegram", "email"]);

type Kind = "qr" | "token" | "oauth" | "code";
type Channel = {
  id: string;
  name: string;
  description: string;
  category: string;
  icon: string;
  badge?: string;
  kind: Kind;
  connected: boolean;
  account: string;
  steps: string[];
  /** если у канала несколько типов подключения — какое окно выбора открыть */
  family?: PickerFamily;
};

const SOURCE = channelCatalog as unknown as Channel[];

const KIND_LABEL: Record<Kind, string> = {
  qr: t("Подключение по QR-коду"),
  token: t("Подключение по токену"),
  oauth: t("Авторизация в сервисе"),
  code: t("Код для вставки на сайт"),
};

const accountFor = (c: Channel) => {
  if (c.kind === "token") return t("Токен принят · ждёт проверки");
  if (c.kind === "qr") return t("Основной номер компании");
  if (c.kind === "code") return t("Вебхук активен");
  return t("Аккаунт подключён");
};

/** псевдо-QR: детерминированный узор с тремя «глазками», чтобы блок выглядел как настоящий код */
const QR_SIDE = 21;
const qrCell = (x: number, y: number) => {
  const corners: [number, number][] = [
    [0, 0],
    [QR_SIDE - 7, 0],
    [0, QR_SIDE - 7],
  ];
  for (const [ox, oy] of corners) {
    if (x >= ox && x < ox + 7 && y >= oy && y < oy + 7) {
      const lx = x - ox;
      const ly = y - oy;
      const edge = lx === 0 || lx === 6 || ly === 0 || ly === 6;
      const core = lx >= 2 && lx <= 4 && ly >= 2 && ly <= 4;
      return edge || core;
    }
  }
  return (x * 3 + y * 5 + x * y * 2) % 7 < 3;
};

function FakeQr() {
  return (
    <div className="mx-auto w-36 rounded-btn bg-background p-2 ring-1 ring-border">
      <div className="grid grid-cols-[repeat(21,minmax(0,1fr))] gap-px">
        {Array.from({ length: QR_SIDE * QR_SIDE }, (_, i) => (
          <span
            key={i}
            className={cn("aspect-square", qrCell(i % QR_SIDE, Math.floor(i / QR_SIDE)) ? "bg-foreground" : "bg-transparent")}
            aria-hidden
          />
        ))}
      </div>
    </div>
  );
}

function ChannelsDemo({ title, subtitle }: SectionProps) {
  const { brand } = usePage();
  const [items, setItems] = useState<Channel[]>(() => SOURCE.map((c) => ({ ...c, steps: [...c.steps] })));
  const [cat, setCat] = useState("all");
  const [query, setQuery] = useState("");
  const [draft, setDraft] = useState<Channel | null>(null);
  const [wizard, setWizard] = useState<WizardChannel | null>(null);
  const [picker, setPicker] = useState<PickerFamily | null>(null);
  const [hub, setHub] = useState<HubChannel[]>([]);
  const [canManage, setCanManage] = useState(false);
  const [gateway, setGateway] = useState(true);
  const [telegram, setTelegram] = useState(true);
  const [mail, setMail] = useState(true);
  const [existing, setExisting] = useState<HubChannel | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [acting, setActing] = useState<string | null>(null);
  const [secret, setSecret] = useState("");
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState("");
  const [copied, setCopied] = useState(false);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => () => clearTimeout(toastTimer.current), []);

  const loadHub = useCallback(() => {
    channelsApi
      .list()
      .then((r) => {
        setHub(r.items.filter((c) => LIVE_KINDS.has(c.kind))); // архив Wazzup — на экране Zoho, к шлюзу он не относится
        setCanManage(r.canManage);
        setGateway(r.gateway);
        setTelegram(r.telegram);
        setMail(r.mail);
      })
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    loadHub();
    return onServerEvent("channel.state", loadHub);
  }, [loadHub]);

  const connectedHub = hub.filter((c) => c.status === "connected" && c.kind === "whatsapp");
  const connectedTg = hub.filter((c) => c.status === "connected" && c.kind === "telegram");
  const connectedMail = hub.filter((c) => c.kind === "email" && c.status !== "disconnected");

  const flash = (text: string) => {
    setToast(text);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(""), 4200);
  };

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    const live = items.map((c) =>
      c.id === "whatsapp"
        ? { ...c, connected: connectedHub.length > 0, account: connectedHub.map((h) => h.name || h.phone).filter(Boolean).join(", ") }
        : c.id === "telegram"
          ? { ...c, connected: connectedTg.length > 0, account: connectedTg.map((h) => h.name || h.phone).filter(Boolean).join(", ") }
          : c.id === "email"
            ? { ...c, connected: connectedMail.length > 0, account: connectedMail.map((h) => h.name).join(", ") }
            : c,
    );
    return live.filter((c) => {
      if (cat !== "all" && c.category !== cat) return false;
      if (!q) return true;
      return `${c.name} ${c.description}`.toLowerCase().includes(q);
    });
  }, [items, cat, query, connectedHub, connectedTg, connectedMail]);

  const connectedCount =
    items.filter((c) => c.connected && c.id !== "whatsapp" && c.id !== "telegram" && c.id !== "email").length +
    (connectedHub.length ? 1 : 0) +
    (connectedTg.length ? 1 : 0) +
    (connectedMail.length ? 1 : 0);
  const countIn = (id: string) => (id === "all" ? items.length : items.filter((c) => c.category === id).length);

  const open = (c: Channel) => {
    if (c.family) {
      setPicker(c.family);
      return;
    }
    if (c.id === "email") {
      setExisting(null);
      setWizard("email");
      return;
    }
    setDraft(c);
    setSecret("");
    setCopied(false);
  };

  const pickKind = (kind: ChannelPick) => {
    setPicker(null);
    setWizard(kind);
  };

  const connectFromWizard = (account: string) => {
    if (!wizard) return;
    const kind = wizard;
    if (kind === "whatsapp") {
      loadHub();
      flash(t("WhatsApp {account}: номер подключён — диалоги уже идут во «Входящие»", { account }));
      return;
    }
    if (kind === "telegram") {
      loadHub();
      flash(t("Telegram {account}: аккаунт подключён — диалоги уже идут во «Входящие»", { account }));
      return;
    }
    if (kind === "email") {
      loadHub();
      flash(t("Почта {account}: ящик подключён — переписка с клиентами подтягивается во «Входящие»", { account }));
      return;
    }
    const id = WIZARD_CATALOG_ID[kind];
    setItems((prev) => prev.map((c) => (c.id === id ? { ...c, connected: true, account } : c)));
    flash(t("{name}: канал подключён — диалоги уже идут во «Входящие»", { name: CHANNEL_META[kind].name }));
  };

  const confirm = () => {
    if (!draft) return;
    if (draft.kind === "token" && secret.trim().length < 6) return;
    setBusy(true);
    const target = draft;
    setTimeout(() => {
      setItems((prev) => prev.map((c) => (c.id === target.id ? { ...c, connected: true, account: accountFor(target) } : c)));
      setBusy(false);
      setDraft(null);
      flash(t("{name}: канал подключён — диалоги уже идут во «Входящие»", { name: target.name }));
    }, 600);
  };

  const saveBot = async (ch: HubChannel, value: string) => {
    const botName = value.trim();
    if (botName === ch.settings.botName) return;
    try {
      const updated = await channelsApi.update(ch.id, { botName });
      setHub((list) => list.map((x) => (x.id === ch.id ? updated : x)));
      flash(
        botName
          ? t("{name}: диалоги без ответственного ведёт бот {bot}", { name: ch.name || ch.phone || "", bot: botName })
          : t("{name}: бот с номера убран", { name: ch.name || ch.phone || "" }),
      );
    } catch (err) {
      flash(apiErrorText(err));
    }
  };

  const hubAction = async (ch: HubChannel, action: "logout" | "delete" | "reconnect") => {
    if (action === "reconnect") {
      setExisting(ch);
      setWizard(ch.kind === "telegram" ? "telegram" : ch.kind === "email" ? "email" : "whatsapp");
      return;
    }
    setActing(ch.id);
    try {
      if (action === "logout") {
        await channelsApi.logout(ch.id);
        flash(t("{name}: номер отключён, история диалогов сохранена", { name: ch.name || ch.phone || "" }));
      } else {
        await channelsApi.remove(ch.id);
        flash(t("{name}: канал удалён, диалоги остались в архиве", { name: ch.name || ch.phone || "" }));
      }
      setConfirmDelete(null);
      loadHub();
    } catch (err) {
      flash(apiErrorText(err));
    } finally {
      setActing(null);
    }
  };

  const disconnect = (c: Channel) => {
    if (c.id === "whatsapp" || c.id === "telegram") {
      setPicker(c.id);
      return;
    }
    if (c.id === "email") {
      setExisting(null);
      setWizard("email");
      return;
    }
    setItems((prev) => prev.map((x) => (x.id === c.id ? { ...x, connected: false, account: "" } : x)));
    flash(t("{name}: канал отключён, история диалогов сохранена", { name: c.name }));
  };

  const copyWebhook = async () => {
    const url = "https://api.hub.example/v1/webhooks/inbound/hub-7f31";
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  const canConfirm = Boolean(draft) && !busy && (draft?.kind !== "token" || secret.trim().length >= 6);

  return (
    <Band id="channels" tone="muted">
      <Head title={title} subtitle={subtitle} />
      <div {...rv()} className="flash-card relative overflow-hidden rounded-card border border-border bg-card !p-0">
        {/* полоска «окна приложения» */}
        <div className="flex flex-wrap items-center gap-2.5 border-b border-border px-4 py-3">
          <span className="flex gap-1.5" aria-hidden>
            <span className="size-2.5 rounded-full bg-foreground/20" />
            <span className="size-2.5 rounded-full bg-foreground/20" />
            <span className="size-2.5 rounded-full bg-foreground/20" />
          </span>
          <span className="flash-mono ms-1 truncate text-[11px] text-muted-foreground">{str(brand) || "Hub"} {t("/ настройки → каналы")}</span>
          <span className="ms-auto inline-flex items-center gap-2 whitespace-nowrap text-[11px] text-muted-foreground">
            <span className="size-1.5 rounded-full bg-emerald-400" aria-hidden />
            {t("Подключено {n} из {total}", { n: connectedCount, total: items.length })}
          </span>
        </div>

        <div className="grid lg:grid-cols-[15rem_minmax(0,1fr)]">
          {/* ── категории ── */}
          <div className="hidden flex-col gap-1 border-border p-3 lg:flex lg:border-e">
            <p className="px-2 pb-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{t("Каталог каналов")}</p>
            {channelCategories.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => setCat(c.id)}
                aria-pressed={cat === c.id}
                className={cn(
                  "flex items-center gap-2.5 rounded-btn px-2.5 py-2 text-start text-[13px] font-medium transition",
                  cat === c.id ? "bg-primary/15 text-primary" : "text-muted-foreground hover:bg-muted hover:text-foreground",
                )}
              >
                <i className={cn(c.icon, "text-base leading-none")} aria-hidden />
                <span className="flex-1 truncate">{c.label}</span>
                <span className="text-[11px] tabular-nums opacity-70">{countIn(c.id)}</span>
              </button>
            ))}
            <div className="mt-3 rounded-btn border border-border bg-background/50 p-3">
              <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-primary">
                <ShieldCheck className="size-3.5" aria-hidden />
                {t("Доступы")}
              </p>
              <p className="mt-1.5 text-[11px] leading-relaxed text-muted-foreground">
                {t("Номера и токены видит только администратор. Продавцы получают диалоги, а не пароли.")}
              </p>
            </div>
          </div>

          {/* ── карточки каналов ── */}
          <div className="min-w-0 p-3.5 md:p-5">
            <div className="flex flex-col gap-3">
              <div className="flex gap-1.5 overflow-x-auto pb-1 lg:hidden">
                {channelCategories.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setCat(c.id)}
                    aria-pressed={cat === c.id}
                    className={cn(
                      "inline-flex flex-none items-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1.5 text-[11px] font-semibold transition",
                      cat === c.id ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground hover:text-foreground",
                    )}
                  >
                    <i className={c.icon} aria-hidden />
                    {c.label}
                  </button>
                ))}
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <label className="relative block min-w-0 flex-1 basis-56">
                  <span className="sr-only">{t("Поиск по каналам")}</span>
                  <Search className="pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
                  <input
                    type="search"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={t("Найти канал: WhatsApp, почта, звонки…")}
                    className="h-10 w-full box-border rounded-btn border border-border bg-background ps-9 pe-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/25"
                  />
                </label>
                <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-primary/10 px-3 py-1.5 text-[11px] font-semibold text-primary">
                  <i className="ri-add-line" aria-hidden />
                  {t("Добавить свой канал")}
                </span>
              </div>
            </div>

            {hub.length > 0 ? (
              <div className="mt-4 rounded-card border border-primary/30 bg-primary/5 p-3">
                <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-primary">
                  <i className="ri-chat-3-line text-[13px]" aria-hidden />
                  {t("Подключённые номера WhatsApp, аккаунты Telegram и почтовые ящики")}
                </p>
                <ul className="mt-2 flex flex-col gap-1.5">
                  {hub.map((ch) => {
                    const ok = ch.status === "connected";
                    const pending = ch.status === "connecting" || ch.status === "qr" || ch.status === "pairing";
                    const tg = ch.kind === "telegram";
                    const mailbox = ch.kind === "email";
                    return (
                      <li key={ch.id} className="flex flex-wrap items-center gap-2 rounded-btn border border-border bg-card px-3 py-2">
                        <span className={cn("size-2 flex-none rounded-full", ok ? "bg-emerald-500" : pending ? "bg-amber-400" : "bg-red-400")} aria-hidden />
                        <i className={cn("flex-none text-[14px]", mailbox ? "ri-mail-line text-primary" : tg ? "ri-telegram-line text-sky-600" : "ri-whatsapp-line text-emerald-600")} aria-hidden />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-[12.5px] font-semibold">{ch.name || ch.phone || (mailbox ? t("Почта") : tg ? "Telegram" : "WhatsApp")}</span>
                          <span className="block truncate text-[10.5px] text-muted-foreground">
                            {ch.phone ? `${ch.phone} · ` : ""}
                            {mailbox && pending && !ch.settings.backfilled ? t("загружаем историю писем") : (CHANNEL_STATUS_LABEL[ch.status] ?? ch.status)}
                            {ch.statusError && !ok ? ` (${ch.statusError})` : ""} · {relTime(ch.statusAt)}
                            {mailbox && !ch.settings.userId ? ` · ${t("сотрудника с таким адресом нет в Hub")}` : ""}
                          </span>
                        </span>
                        {canManage ? (
                          <span className="flex flex-wrap items-center gap-1.5">
                            {!ok ? (
                              <button
                                type="button"
                                onClick={() => hubAction(ch, "reconnect")}
                                disabled={acting === ch.id}
                                className="inline-flex h-7 cursor-pointer items-center gap-1 whitespace-nowrap rounded-btn bg-primary px-2.5 text-[11px] font-semibold text-primary-foreground transition hover:brightness-110 disabled:opacity-50"
                              >
                                <RefreshCw className="size-3" aria-hidden />
                                {pending && !mailbox ? t("Показать код") : t("Переподключить")}
                              </button>
                            ) : null}
                            {ok ? (
                              <button
                                type="button"
                                onClick={() => hubAction(ch, "logout")}
                                disabled={acting === ch.id}
                                className="inline-flex h-7 cursor-pointer items-center whitespace-nowrap rounded-btn border border-border px-2.5 text-[11px] font-semibold text-muted-foreground transition hover:border-red-400/60 hover:text-red-400 disabled:opacity-50"
                              >
                                {t("Отключить")}
                              </button>
                            ) : null}
                            {confirmDelete === ch.id ? (
                              <>
                                <span className="text-[11px] text-muted-foreground">{t("Удалить канал?")}</span>
                                <button
                                  type="button"
                                  onClick={() => hubAction(ch, "delete")}
                                  disabled={acting === ch.id}
                                  className="inline-flex h-7 cursor-pointer items-center whitespace-nowrap rounded-btn bg-red-500 px-2.5 text-[11px] font-semibold text-white transition hover:brightness-110 disabled:opacity-50"
                                >
                                  {t("Да, удалить")}
                                </button>
                                <button type="button" onClick={() => setConfirmDelete(null)} className="inline-flex h-7 cursor-pointer items-center rounded-btn border border-border px-2 text-[11px] font-semibold text-muted-foreground">
                                  {t("Нет")}
                                </button>
                              </>
                            ) : (
                              <button
                                type="button"
                                onClick={() => setConfirmDelete(ch.id)}
                                aria-label={t("Удалить канал")}
                                title={t("Удалить канал (диалоги останутся)")}
                                className="grid size-7 cursor-pointer place-items-center rounded-btn border border-border text-muted-foreground transition hover:border-red-400/60 hover:text-red-400"
                              >
                                <Trash2 className="size-3.5" aria-hidden />
                              </button>
                            )}
                          </span>
                        ) : null}
                        {canManage && !tg && !mailbox ? (
                          <label className="flex w-full items-center gap-2 border-t border-border/60 pt-1.5 text-[10.5px] text-muted-foreground" title={t("Если с этого номера отвечает бот, укажите его имя: диалоги без ответственного будут помечены «ведёт бот», а продавец сможет их перехватить")}>
                            <i className="ri-robot-2-line text-[12px]" aria-hidden />
                            {t("Бот на номере")}
                            <input
                              key={`${ch.id}-${ch.settings.botName}`}
                              defaultValue={ch.settings.botName}
                              placeholder={t("например, Никита")}
                              maxLength={60}
                              onBlur={(e) => void saveBot(ch, e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") (e.target as HTMLInputElement).blur();
                              }}
                              className="h-6 w-36 rounded-btn border border-border bg-background px-2 text-[11px] text-foreground focus:border-primary focus:outline-none"
                            />
                            {ch.settings.botName ? <span className="text-violet-600">{t("диалоги без ответственного ведёт бот")}</span> : null}
                          </label>
                        ) : null}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : null}
            {!gateway ? <p className="mt-3 text-[11px] text-amber-600">{t("Шлюз WhatsApp на сервере не настроен — подключение номеров недоступно.")}</p> : null}
            {!mail ? <p className="mt-3 text-[11px] text-amber-600">{t("Почта не настроена на сервере: нужен ключ сервисного аккаунта Google (делегирование домена) — после этого ящики сотрудников подключаются по адресу.")}</p> : null}
            {!telegram ? <p className="mt-3 text-[11px] text-amber-600">{t("Ключи приложения Telegram ещё не введены — нажмите «Подключить» у Telegram: владелец или администратор вводит их один раз, дальше все подключаются по QR.")}</p> : null}

            <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {visible.map((c) => (
                <article
                  key={c.id}
                  className={cn(
                    "flex flex-col gap-3 rounded-card border p-4 transition",
                    c.connected ? "border-primary/45 bg-primary/5" : "border-border bg-background/40 hover:border-primary/40",
                  )}
                >
                  <div className="flex items-start justify-between gap-2">
                    <span
                      className={cn(
                        "grid size-10 flex-none place-items-center rounded-btn text-xl leading-none",
                        c.connected ? "bg-primary text-primary-foreground" : "bg-primary/10 text-primary",
                      )}
                      aria-hidden
                    >
                      <i className={c.icon} />
                    </span>
                    <span className="flex flex-wrap items-center justify-end gap-1">
                      {c.badge ? (
                        <span className="rounded-full bg-background/70 px-2 py-0.5 text-[10px] font-semibold text-muted-foreground ring-1 ring-border">{c.badge}</span>
                      ) : null}
                      {c.connected ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-semibold text-primary">
                          <Check className="size-2.5" strokeWidth={3} aria-hidden />
                          {t("Подключено")}
                        </span>
                      ) : null}
                    </span>
                  </div>

                  <div className="min-w-0">
                    <h3 className="text-sm font-semibold leading-snug">{c.name}</h3>
                    <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-muted-foreground">{c.description}</p>
                  </div>

                  <div className="mt-auto flex items-center justify-between gap-2 border-t border-border/60 pt-2.5">
                    <span className="min-w-0 truncate text-[11px] text-muted-foreground">{c.connected ? c.account : t("Не подключён")}</span>
                    {c.connected && (c.id === "whatsapp" || c.id === "telegram" || c.id === "email") ? (
                      <button
                        type="button"
                        onClick={() => open(c)}
                        className="flex-none whitespace-nowrap rounded-btn border border-primary/40 px-2.5 py-1.5 text-[11px] font-semibold text-primary transition hover:bg-primary/10"
                      >
                        {c.id === "telegram" ? t("Ещё аккаунт") : c.id === "email" ? t("Ещё ящик") : t("Ещё номер")}
                      </button>
                    ) : c.connected ? (
                      <button
                        type="button"
                        onClick={() => disconnect(c)}
                        className="flex-none whitespace-nowrap rounded-btn border border-border px-2.5 py-1.5 text-[11px] font-semibold text-muted-foreground transition hover:border-red-400/60 hover:text-red-400"
                      >
                        {t("Отключить")}
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => open(c)}
                        className="flex-none whitespace-nowrap rounded-btn bg-primary px-3 py-1.5 text-[11px] font-semibold text-primary-foreground transition hover:brightness-110"
                      >
                        {t("Подключить")}
                      </button>
                    )}
                  </div>
                </article>
              ))}
            </div>

            {visible.length === 0 ? (
              <p className="py-10 text-center text-sm text-muted-foreground">{t("Канал не найден. Попробуйте другое название или категорию.")}</p>
            ) : null}

            <p className="mt-4 border-t border-border/60 pt-3 text-[11px] text-muted-foreground">
              {t("WhatsApp, личный Telegram и почта Gmail Workspace подключаются по-настоящему. Остальные каналы, включая Telegram-бота, пока имитируются в макете — они появятся на следующих этапах.")}
            </p>
          </div>
        </div>

        {picker ? <ChannelPicker family={picker} onPick={pickKind} onClose={() => setPicker(null)} /> : null}

        {wizard ? (
          <ChannelWizard
            channel={wizard}
            existing={existing}
            onClose={() => {
              setWizard(null);
              setExisting(null);
              loadHub();
            }}
            onConnected={connectFromWizard}
          />
        ) : null}

        {toast ? (
          <div
            role="status"
            className="pointer-events-none absolute inset-x-3 bottom-3 z-10 mx-auto flex max-w-md items-start gap-2 rounded-btn border border-primary/40 bg-background/95 px-3.5 py-2.5 text-[12px] text-foreground shadow-lift backdrop-blur"
          >
            <Check className="mt-0.5 size-3.5 flex-none text-primary" strokeWidth={3} aria-hidden />
            {toast}
          </div>
        ) : null}

        {/* ── окно подключения канала ── */}
        {draft ? (
          <div className="absolute inset-0 z-20 flex items-end justify-center bg-black/65 p-3 backdrop-blur-sm sm:items-center md:p-6">
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="channel-dialog-title"
              className="relative max-h-full w-full max-w-lg overflow-y-auto rounded-card border border-border bg-card p-5 shadow-lift md:p-6"
            >
              <button
                type="button"
                onClick={() => setDraft(null)}
                aria-label={t("Закрыть окно подключения")}
                className="absolute end-3 top-3 grid size-8 place-items-center rounded-btn text-muted-foreground transition hover:bg-muted hover:text-foreground"
              >
                <X className="size-4" aria-hidden />
              </button>

              <div className="flex items-start gap-3 pe-8">
                <span className="grid size-11 flex-none place-items-center rounded-btn bg-primary/10 text-2xl leading-none text-primary" aria-hidden>
                  <i className={draft.icon} />
                </span>
                <div className="min-w-0">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{KIND_LABEL[draft.kind]}</p>
                  <h3 id="channel-dialog-title" className="mt-1 text-lg font-semibold leading-snug">
                    {t("Подключить {name}", { name: draft.name })}
                  </h3>
                </div>
              </div>

              <ol className="mt-4 flex flex-col gap-2.5">
                {draft.steps.map((s, i) => (
                  <li key={s} className="flex items-start gap-2.5 text-[13px] leading-relaxed text-muted-foreground">
                    <span className="mt-0.5 grid size-5 flex-none place-items-center rounded-full bg-primary/15 text-[10px] font-bold text-primary tabular-nums" aria-hidden>
                      {i + 1}
                    </span>
                    {s}
                  </li>
                ))}
              </ol>

              <div className="mt-4 rounded-btn border border-border bg-background/60 p-3.5">
                {draft.kind === "qr" ? (
                  <div className="flex flex-col items-center gap-2">
                    <FakeQr />
                    <p className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                      <QrCode className="size-3.5 text-primary" aria-hidden />
                      {t("Код обновляется каждые 60 секунд")}
                    </p>
                  </div>
                ) : null}

                {draft.kind === "token" ? (
                  <div className="grid gap-2">
                    <label htmlFor="channel-token" className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                      {t("Токен доступа")}
                    </label>
                    <input
                      id="channel-token"
                      value={secret}
                      onChange={(e) => setSecret(e.target.value)}
                      placeholder={t("Например, 7724190345:AAH8f2kLm9pQrStUvWxYz")}
                      autoComplete="off"
                      className="h-10 w-full box-border rounded-btn border border-border bg-background px-3 font-mono text-[12px] text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/25"
                    />
                    <p className="text-[11px] text-muted-foreground">
                      {secret.trim().length >= 6 ? t("Токен выглядит корректно — можно подключать.") : t("Токен хранится в защищённом виде и доступен только администратору.")}
                    </p>
                  </div>
                ) : null}

                {draft.kind === "oauth" ? (
                  <p className="flex items-start gap-2 text-[12px] leading-relaxed text-muted-foreground">
                    <ShieldCheck className="mt-0.5 size-4 flex-none text-primary" aria-hidden />
                    {t("Откроем окно авторизации {name}. Hub запросит только доступ к сообщениям и имени профиля — публиковать ничего не сможем.", { name: draft.name })}
                  </p>
                ) : null}

                {draft.kind === "code" ? (
                  <div className="grid gap-2">
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t("Адрес вебхука")}</span>
                    <div className="flex items-center gap-2">
                      <code className="min-w-0 flex-1 truncate rounded-btn bg-muted px-2.5 py-2 font-mono text-[11px] text-foreground">
                        https://api.hub.example/v1/webhooks/inbound/hub-7f31
                      </code>
                      <button
                        type="button"
                        onClick={copyWebhook}
                        className="inline-flex h-9 flex-none items-center gap-1.5 whitespace-nowrap rounded-btn border border-border px-2.5 text-[11px] font-semibold text-muted-foreground transition hover:text-foreground"
                      >
                        {copied ? <Check className="size-3.5 text-primary" aria-hidden /> : <Copy className="size-3.5" aria-hidden />}
                        {copied ? t("Скопировано") : t("Копировать")}
                      </button>
                    </div>
                    <p className="text-[11px] text-muted-foreground">{t("События приходят в формате JSON, подпись — в заголовке X-Hub-Signature.")}</p>
                  </div>
                ) : null}
              </div>

              <div className="mt-4 flex flex-col-reverse items-stretch gap-2 sm:flex-row sm:items-center sm:justify-end">
                <button
                  type="button"
                  onClick={() => setDraft(null)}
                  className="inline-flex h-10 items-center justify-center whitespace-nowrap rounded-btn border border-border px-4 text-[13px] font-semibold text-muted-foreground transition hover:text-foreground"
                >
                  {t("Отмена")}
                </button>
                <button
                  type="button"
                  onClick={confirm}
                  disabled={!canConfirm}
                  className="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-btn bg-primary px-4 text-[13px] font-semibold text-primary-foreground transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {busy ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
                  {busy ? t("Подключаем…") : t("Подключить канал")}
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </Band>
  );
}

export default ChannelsDemo;